import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faCog, faUtensils, faBatteryFull, faChevronDown, faCheck, faStar, faUsers, faChartLine, faCogs } from '@fortawesome/free-solid-svg-icons';
import robotImage from './assets/robot.png';

const App = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [emailStatus, setEmailStatus] = useState({ show: false, success: false, message: '' });
  const form = useRef();

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId).scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setEmailStatus({ show: true, success: false, message: 'Sending...' });
      
      const formData = {
        name: e.target.user_name.value,
        email: e.target.user_email.value,
        message: e.target.message.value
      };

      const response = await fetch('http://localhost:3006/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setEmailStatus({
          show: true,
          success: true,
          message: 'Message sent successfully!'
        });
        
        // Clear form
        form.current.reset();
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
      
      // Hide status message after 5 seconds
      setTimeout(() => {
        setEmailStatus({ show: false, success: false, message: '' });
      }, 5000);

    } catch (error) {
      setEmailStatus({
        show: true,
        success: false,
        message: 'Failed to send message. Please try again.'
      });
      
      // Hide error message after 5 seconds
      setTimeout(() => {
        setEmailStatus({ show: false, success: false, message: '' });
      }, 5000);
    }
  };

  const specifications = [
    { label: "Height", value: "150 cm" },
    { label: "Weight", value: "45 kg" },
    { label: "Battery Life", value: "12 hours" },
    { label: "Loading Capacity", value: "15 kg" },
    { label: "Navigation", value: "LiDAR + AI" },
    { label: "Display", value: "10.1\" Touch LCD" }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Restaurant Owner",
      text: "Our efficiency has doubled since we started using RoboWaiter. Our customers love the futuristic experience!"
    },
    {
      name: "Michael Brown",
      role: "Hotel Manager",
      text: "The RoboWaiter has been a game-changer for our room service. Highly reliable and always on time."
    },
    {
      name: "Emily Davis",
      role: "Event Planner",
      text: "Our events run smoother than ever with RoboWaiters handling the catering. They're a hit with our clients!"
    }
  ];

  const pricingPlans = [
    {
      title: "Starter",
      price: "$X/mo",
      features: [
        "1 RoboWaiter",
        "Basic Maintenance",
        "24/7 Support",
        "Software Updates"
      ]
    },
    {
      title: "Professional",
      price: "$X/mo",
      features: [
        "3 RoboWaiters",
        "Advanced Analytics",
        "Priority Support",
        "Custom Branding"
      ]
    },
    {
      title: "Enterprise",
      price: "Custom",
      features: [
        "10+ RoboWaiters",
        "Dedicated Account Manager",
        "Custom Integrations",
        "On-site Training"
      ]
    }
  ];

  return (
    <Container>
      <Navbar>
        <Logo>RoboWaiter</Logo>
        <NavLinks>
          {['home', 'features', 'specifications', 'pricing', 'testimonials', 'demo', 'contact'].map((section) => (
            <NavLink
              key={section}
              onClick={() => scrollToSection(section)}
              $active={activeSection === section}
              as={motion.a}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {section.charAt(0).toUpperCase() + section.slice(1)}
            </NavLink>
          ))}
        </NavLinks>
      </Navbar>

      <Hero id="home">
        <HeroContent>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <HeroTitle>The Future of Restaurant Service</HeroTitle>
            <HeroSubtitle>
              Efficient. Reliable. Revolutionary.
            </HeroSubtitle>
            <CTAButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('contact')}
            >
              Get Started
            </CTAButton>
          </motion.div>
        </HeroContent>
        <HeroImage
          as={motion.div}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <motion.img 
            src={robotImage} 
            alt="Waiter Robot"
            animate={{ y: [0, -20, 0] }}
            transition={{ 
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </HeroImage>
        <ScrollIndicator
          onClick={() => scrollToSection('features')}
          as={motion.div}
          animate={{ y: [0, 10, 0] }}
          transition={{ 
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <FontAwesomeIcon icon={faChevronDown} />
        </ScrollIndicator>
      </Hero>

      <Features id="features">
        <FeatureTitle>Advanced Features</FeatureTitle>
        <FeatureGrid>
          {[
            {
              icon: faRobot,
              title: "AI Navigation",
              description: "Smart pathfinding and obstacle avoidance with real-time mapping"
            },
            {
              icon: faCog,
              title: "Precision Control",
              description: "Accurate and stable movement system with dynamic balance"
            },
            {
              icon: faUtensils,
              title: "Multi-tray System",
              description: "Efficient multi-order delivery with smart load distribution"
            },
            {
              icon: faBatteryFull,
              title: "Long Battery Life",
              description: "24/7 operation capability with quick charging technology"
            }
          ].map((feature, index) => (
            <FeatureCard
              key={index}
              as={motion.div}
              whileHover={{ y: -10, scale: 1.02 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <FeatureIcon>
                <FontAwesomeIcon icon={feature.icon} />
              </FeatureIcon>
              <FeatureCardTitle>{feature.title}</FeatureCardTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </Features>

      <Specifications id="specifications">
        <SpecTitle>Technical Specifications</SpecTitle>
        <SpecGrid>
          {specifications.map((spec, index) => (
            <SpecCard
              key={index}
              as={motion.div}
              whileHover={{ y: -10, scale: 1.02 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <SpecLabel>{spec.label}</SpecLabel>
              <SpecValue>{spec.value}</SpecValue>
            </SpecCard>
          ))}
        </SpecGrid>
      </Specifications>

      <Testimonials id="testimonials">
        <TestimonialTitle>What Our Customers Say</TestimonialTitle>
        <TestimonialGrid>
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              as={motion.div}
              whileHover={{ y: -10, scale: 1.02 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <TestimonialText>"{testimonial.text}"</TestimonialText>
              <TestimonialAuthor>
                <FontAwesomeIcon icon={faUsers} />
                <div>
                  <TestimonialName>{testimonial.name}</TestimonialName>
                  <TestimonialRole>{testimonial.role}</TestimonialRole>
                </div>
              </TestimonialAuthor>
              <TestimonialRating>
                {[1, 2, 3, 4, 5].map((star) => (
                  <FontAwesomeIcon key={star} icon={faStar} />
                ))}
              </TestimonialRating>
            </TestimonialCard>
          ))}
        </TestimonialGrid>
      </Testimonials>

      <Pricing id="pricing">
        <PricingSectionTitle>Flexible Pricing Plans</PricingSectionTitle>
        <PricingGrid>
          {pricingPlans.map((plan, index) => (
            <PricingCard
              key={index}
              as={motion.div}
              whileHover={{ y: -10, scale: 1.02 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <PricingHeader>
                <PricingPlanTitle>{plan.title}</PricingPlanTitle>
                <PricingPrice>{plan.price}</PricingPrice>
              </PricingHeader>
              <PricingFeatures>
                {plan.features.map((feature, i) => (
                  <PricingFeature key={i}>
                    <FontAwesomeIcon icon={faCheck} />
                    {feature}
                  </PricingFeature>
                ))}
              </PricingFeatures>
              <PricingButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scrollToSection('contact')}
              >
                Get Started
              </PricingButton>
            </PricingCard>
          ))}
        </PricingGrid>
      </Pricing>

      <Demo id="demo">
        <DemoTitle>Experience the Future</DemoTitle>
        <DemoContent>
          <DemoVideo>
            <iframe
              src="https://www.youtube.com/embed/TodN78f-W_E"
              title="Waiter Robot Demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </DemoVideo>
          <DemoInfo>
            <DemoFeature>
              <FontAwesomeIcon icon={faUsers} />
              <div>
                <DemoFeatureTitle>Enhanced Customer Experience</DemoFeatureTitle>
                <DemoFeatureText>Our robots provide consistent, friendly service that customers love.</DemoFeatureText>
              </div>
            </DemoFeature>
            <DemoFeature>
              <FontAwesomeIcon icon={faChartLine} />
              <div>
                <DemoFeatureTitle>Operational Efficiency</DemoFeatureTitle>
                <DemoFeatureText>Reduce wait times and increase table turnover rates.</DemoFeatureText>
              </div>
            </DemoFeature>
            <DemoFeature>
              <FontAwesomeIcon icon={faCogs} />
              <div>
                <DemoFeatureTitle>Smart Technology</DemoFeatureTitle>
                <DemoFeatureText>Advanced AI ensures smooth operation and adaptability.</DemoFeatureText>
              </div>
            </DemoFeature>
          </DemoInfo>
        </DemoContent>
      </Demo>

      <Contact id="contact">
        <ContactTitle>Get in Touch</ContactTitle>
        <ContactForm ref={form} onSubmit={handleSubmit}>
          <Input type="text" name="user_name" placeholder="Your Name" required />
          <Input type="email" name="user_email" placeholder="Your Email" required />
          <TextArea name="message" placeholder="Your Message" required />
          <SubmitButton
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Send Message
          </SubmitButton>
          <AnimatePresence>
            {emailStatus.show && (
              <StatusMessage
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                $success={emailStatus.success}
              >
                {emailStatus.message}
              </StatusMessage>
            )}
          </AnimatePresence>
        </ContactForm>
      </Contact>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d3436 100%);
  color: white;
  font-family: 'Space Grotesk', sans-serif;
`;

const Navbar = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 5%;
  position: fixed;
  width: 100%;
  top: 0;
  background: rgba(26, 26, 26, 0.9);
  backdrop-filter: blur(10px);
  z-index: 1000;
`;

const Logo = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  background: linear-gradient(45deg, #00f2fe, #4facfe);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
`;

const NavLink = styled.a`
  color: ${props => props.$active ? '#4facfe' : 'white'};
  text-decoration: none;
  font-size: 1.1rem;
  cursor: pointer;
  transition: color 0.3s ease;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    width: ${props => props.$active ? '100%' : '0'};
    height: 2px;
    bottom: -5px;
    left: 0;
    background-color: #4facfe;
    transition: width 0.3s ease;
  }

  &:hover {
    color: #4facfe;
    &:after {
      width: 100%;
    }
  }
`;

const Hero = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 100vh;
  padding: 0 5%;
  position: relative;
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  font-size: 2rem;
  color: #4facfe;
  cursor: pointer;
`;

const HeroContent = styled.div`
  flex: 1;
  padding-right: 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #00f2fe, #4facfe);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  color: #888;
`;

const HeroImage = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    max-width: 100%;
    height: auto;
  }
`;

const CTAButton = styled(motion.button)`
  padding: 1rem 2rem;
  font-size: 1.2rem;
  border: none;
  border-radius: 50px;
  background: linear-gradient(45deg, #00f2fe, #4facfe);
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(79, 172, 254, 0.4);
`;

const Features = styled.section`
  padding: 5rem 5%;
  background: rgba(255, 255, 255, 0.02);
`;

const FeatureTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(45deg, #00f2fe, #4facfe);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  padding: 2rem 0;
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.3s ease;
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #4facfe;
`;

const FeatureCardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: white;
`;

const FeatureDescription = styled.p`
  color: #888;
  line-height: 1.6;
`;

const Specifications = styled.section`
  padding: 5rem 5%;
  background: rgba(0, 0, 0, 0.3);
`;

const SpecTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(45deg, #00f2fe, #4facfe);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SpecGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const SpecCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 1.5rem;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const SpecLabel = styled.h4`
  font-size: 1.2rem;
  color: #4facfe;
  margin-bottom: 0.5rem;
`;

const SpecValue = styled.p`
  font-size: 1.1rem;
  color: white;
`;

const Testimonials = styled.section`
  padding: 5rem 5%;
  background: rgba(255, 255, 255, 0.02);
`;

const TestimonialTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(45deg, #00f2fe, #4facfe);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  padding: 2rem 0;
`;

const TestimonialCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.3s ease;
`;

const TestimonialText = styled.p`
  font-size: 1.2rem;
  color: white;
  margin-bottom: 1rem;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  svg {
    color: #4facfe;
  }
`;

const TestimonialName = styled.h4`
  font-size: 1.2rem;
  color: #4facfe;
`;

const TestimonialRole = styled.p`
  font-size: 1.1rem;
  color: white;
`;

const TestimonialRating = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 1rem;
`;

const Pricing = styled.section`
  padding: 5rem 5%;
  background: rgba(255, 255, 255, 0.02);
`;

const PricingSectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(45deg, #00f2fe, #4facfe);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  padding: 2rem 0;
`;

const PricingCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.3s ease;
`;

const PricingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const PricingPlanTitle = styled.h3`
  font-size: 1.5rem;
  color: white;
`;

const PricingPrice = styled.p`
  font-size: 1.2rem;
  color: #4facfe;
`;

const PricingFeatures = styled.div`
  margin-bottom: 1rem;
`;

const PricingFeature = styled.p`
  font-size: 1.1rem;
  color: white;
  margin-bottom: 0.5rem;
`;

const PricingButton = styled(CTAButton)`
  width: fit-content;
`;

const Demo = styled.section`
  padding: 5rem 5%;
  background: linear-gradient(45deg, #1a1a1a, #0d0d0d);
`;

const DemoTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(45deg, #00f2fe, #4facfe);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const DemoContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DemoVideo = styled.div`
  position: relative;
  padding-top: 56.25%;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 0 30px rgba(0, 242, 254, 0.3);

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const DemoInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const DemoFeature = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 20px rgba(0, 242, 254, 0.2);
  }

  svg {
    font-size: 1.5rem;
    color: #4facfe;
  }
`;

const DemoFeatureTitle = styled.h3`
  font-size: 1.3rem;
  color: white;
  margin-bottom: 0.5rem;
`;

const DemoFeatureText = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
`;

const Contact = styled.section`
  padding: 5rem 5%;
  background: rgba(255, 255, 255, 0.02);
`;

const ContactTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(45deg, #00f2fe, #4facfe);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Input = styled.input`
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: white;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #4facfe;
  }
`;

const TextArea = styled.textarea`
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: white;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #4facfe;
  }
`;

const SubmitButton = styled(CTAButton)`
  width: fit-content;
`;

const StatusMessage = styled(motion.div)`
  margin-top: 1rem;
  padding: 0.8rem;
  border-radius: 5px;
  color: white;
  background-color: ${props => props.$success ? '#4CAF50' : '#f44336'};
  text-align: center;
`;

export default App;
